import {useEffect, useState} from "react";
import {db} from "../db";
import CollectionImage from "../components/CollectionImage";
import Video from "../components/Video";
import Audio from "../components/Audio";
import DoubleRightIcon from "../images/icons/white-double-right.svg";
import DoubleLeftIcon from "../images/icons/white-double-left.svg";
import GreyDoubleRightIcon from "../images/icons/grey-double-right.svg";
import GreyDoubleLeftIcon from "../images/icons/grey-double-left.svg";
import { useCollectionContext, useCollectionContextDispatch } from "../context/CollectionContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { isUndefined } from "cropperjs";
import { useAccountContext } from "../context/AccountContext";
import { getCollectionHashes, updateAccount, getCollectionPaginated } from "../api-helper";
import cardPrivate from '../images/icons/image-card-icons/card-icon-private.svg';
import cardPublic from '../images/icons/image-card-icons/card-icon-public.svg';
import Logo from "../images/logo_light_grey.png";
import PromptSpin from "../images/prompts.lottie";
import { liveQuery } from "dexie";
import { InView } from 'react-intersection-observer';
import Masonry from 'react-masonry-css';
import { fetchMediaType } from "../utils/MediaOperations";

function Placeholder(props){
    return (
        <div className="group w-full rounded-xl overflow-hidden relative bg-gray-400 animate-pulse aspect-square">
            <img className="w-full h-full object-contain" src="https://i.postimg.cc/632gRsSp/Final-logo-Black.png" alt='Loading...'/>
        </div>
    );
}

// Media type detection function
async function getMediaType(base64String) {
    try {
        const cleaned = base64String.trim().replace(/\s/g, '');
        const padded = cleaned + '==='.slice((cleaned.length + 3) % 4);
        const binaryStr = atob(padded);
        const bytes = new Uint8Array(binaryStr.length);

        for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }

        // Generate long hex string from first 32 bytes
        const hex = Array.from(bytes.slice(0, 32))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

        console.log('First 32 bytes hex:', hex);

        // Match signatures even at offset
        const signatures = [
            { type: 'video/mp4', hex: '6674797069736f6d' }, // 'ftypisom'
            { type: 'video/mp4', hex: '667479706d703432' }, // 'ftypmp42'
            { type: 'image/png', hex: '89504e47' },
            { type: 'image/jpeg', hex: 'ffd8ff' },
            { type: 'image/webp', hex: '52494646' },
            { type: 'audio/mpeg', hex: '494433' },
            { type: 'video/webm', hex: '1a45dfa3' },
            { type: 'audio/ogg', hex: '4f676753' },
            { type: 'audio/wav', hex: '52494646' },
            { type: 'application/pdf', hex: '25504446' },
        ];

        const matched = signatures.find(sig => hex.includes(sig.hex.toLowerCase()));
        const mimeType = matched?.type || 'Unknown Media Type';

        if (mimeType.startsWith("image/")) return "image";
        if (mimeType.startsWith("video/")) return "video";
        if (mimeType.startsWith("audio/")) return "audio";

        console.log(`getMediaType returns ${mimeType}`);
        
        return mimeType;
    } catch (error) {
        console.error('Error detecting media type:', error);
        return 'image'; // Default to image if detection fails
    }
}

export default function MyCollection(props) {
    const [loading, setLoadingImages] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [imageList, setImageList] = useState([]);
    const [tempImageList, setTempImageList] = useState([]);
    const [publicImage, setPublicImage] = useState(false);
    const [columnClass, setColumnClass] = useState("cols-4");
    const [redoPromptUid, setRedoPromptUid] = useState("");
    const [inputImage, setInputImage] = useState(null);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [page, setPage] = useState(0);
    const [totalStoredImages, setTotalStoredImages] = useState(0);
    const [totalImages, setTotalImages] = useState(0);
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState("All");
    const [showAll, setShowAll] = useState(false);
    const [saveImages, setSaveImages] = useState(false);
    const navigate = useNavigate();
    const collectionContextDispatch = useCollectionContextDispatch();
    const collectionContext = useCollectionContext();
    const accountContext = useAccountContext();
    const [animations, setAnimations] = useState(false);
    const [empty, setEmpty] = useState(false);
    const [sortMethod, setSortMethod] = useState("Newest");
    const [apiTotalCount, setApiTotalCount] = useState(0);
    const [filteredTotalCount, setFilteredTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [infiniteScrollPage, setInfiniteScrollPage] = useState(1);

    const getTotalStoredImages = async () => {
        setLoadingImages(true);
        
        // Get total count from API first page request
        if (accountContext.loggedIn && accountContext.accountDetails?.accountNo) {
            try {
                await refreshImages();
            } catch (error) {
                console.error("Error fetching total images:", error);
            }
        } else {
            // Not logged in, show empty collection 
            setImageList([]);
            setTotalImages(0);
            setFilteredTotalCount(0);
        }
        
        setLoadingImages(false);
    };

    const toFileUrl = (filePath) => {
        const fixed = filePath.replace(/\\/g, '/'); // \ -> /
        const encodedURL = `safe-file://${encodeURI(fixed)}`;
        return encodedURL;
      }

    const loadMoreImages = async () => {
        if (!loadingMore && hasMore && showAll) {
            setLoadingMore(true);
            setInfiniteScrollPage(prev => prev + 1);
            await refreshImages(true);
        }
    };

    // useEffect(() => {
    //     const subscription = liveQuery(() => db.collectedImages.count())
    //         .subscribe(count => {
    //             console.log("My Collection Images: ", count);
    //             if (count > 1) {
    //                 refreshImages();
    //             }
    //         });
    
    //     // Cleanup function to unsubscribe when the component unmounts
    //     return () => subscription.unsubscribe();
    // }, [db]);

    const refreshImages = async (append = false) => {
        console.log('[REFRESH IMAGES] Starting image pull for My Collection');
        setLoadingImages(true)
        // Use API for pagination
        if (!append) {
            setPageLoading(true);
        }
        try {
            const accountNo = accountContext.accountDetails.accountNo;
            
            // When filtering is active, we need to fetch more data to get accurate filtered count
            const isFiltering = category !== "All" || sortMethod.includes("Non-Tagged") || sortMethod.includes("SH-Images");
            // For infinite scroll, always use fixed page size
            const totalPerPage = showAll ? 28 : (isFiltering ? 1000 : 20); // Use 28 for infinite scroll
            const pageNo = showAll ? infiniteScrollPage : (isFiltering ? 1 : page + 1); // Use infiniteScrollPage for infinite scroll
            
            const response = await getCollectionPaginated(accountNo, totalPerPage, pageNo);
            
            if (response && response.data) {
                console.log('[COLLECTION REFRESH] Response of getColletionaginated: ', response);
                
                let collectionImages = response.data;
                
                // Update total count if provided by API (moved up to ensure it's set before hasMore calculation)
                if (response.totalCount !== undefined) {
                    setApiTotalCount(response.totalCount);
                    setTotalStoredImages(response.totalCount);
                } else if (response.total !== undefined) {
                    setApiTotalCount(response.total);
                    setTotalStoredImages(response.total);
                } else if (response.recordCount !== undefined) {
                    setApiTotalCount(parseInt(response.recordCount));
                    setTotalStoredImages(parseInt(response.recordCount));
                }
                
                // Apply client-side filtering based on category
                if (category !== "All") {
                    if (category === "Public" || category === "Private") {
                        collectionImages = collectionImages.filter(image => image.public === (category === "Public"));
                    } else if (category === "PublicTags"){
                        collectionImages = collectionImages.filter(image => image.public && !isUndefined(image.tag) && image.tag.length > 0);
                    } else if (category === "PrivateTags") {
                        collectionImages = collectionImages.filter(image => !image.public && !isUndefined(image.tag) && image.tag.length > 0);
                    } else if (category === "PublicNoTags") {
                        collectionImages = collectionImages.filter(image => image.public && (isUndefined(image.tag) || image.tag.length === 0));
                    } else if (category === "PrivateNoTags") {
                        collectionImages = collectionImages.filter(image => !image.public && (isUndefined(image.tag) || image.tag.length === 0));
                    } else {
                        collectionImages = collectionImages.filter(image => !isUndefined(image.tag) && image.tag.toLowerCase().includes(category.toLowerCase()));
                    }
                }
                
                // Apply client-side sorting
                switch(sortMethod){
                    case "Newest": 
                        // Assuming API returns newest first by default
                        break;
                    case "Oldest": 
                        collectionImages = collectionImages.reverse(); 
                        break;
                    case "Non-Tagged-Asc": 
                        collectionImages = collectionImages.filter(image => isUndefined(image.tag) || image.tag === ""); 
                        break;
                    case "Non-Tagged-Desc": 
                        collectionImages = collectionImages.filter(image => isUndefined(image.tag) || image.tag === "").reverse(); 
                        break;
                    case "SH-Images-Asc": 
                        collectionImages = collectionImages.filter(image => !isUndefined(image.model) && image.model.includes('SH')); 
                        break;
                    case "SH-Images-Desc": 
                        collectionImages = collectionImages.filter(image => !isUndefined(image.model) && image.model.includes('SH')).reverse(); 
                        break;
                }
                
                // Store the filtered total count before pagination
                const filteredTotal = collectionImages.length;
                setFilteredTotalCount(filteredTotal);
                
                // Apply client-side pagination if we're filtering
                if (isFiltering && !showAll && !append) {
                    const startIndex = page * 20;
                    const endIndex = startIndex + 20;
                    collectionImages = collectionImages.slice(startIndex, endIndex);
                }
                
                // For infinite scroll append mode
                if (append && showAll) {
                    setImageList(prevList => [...prevList, ...collectionImages]);
                    setTotalImages(prevTotal => prevTotal + collectionImages.length);
                    // Check if we have more pages
                    const totalReceived = imageList.length + collectionImages.length;
                    const expectedTotal = isFiltering ? filteredTotalCount : (apiTotalCount || totalStoredImages);
                    setHasMore(collectionImages.length === 28 && totalReceived < expectedTotal);
                    setLoadingImages(false);

                } else {
                    setTotalImages(collectionImages.length);
                    setImageList(collectionImages);
                    // Reset infinite scroll state when not appending
                    setInfiniteScrollPage(1);
                    setHasMore(true);
                    setLoadingImages(false);
                }
                
                // If we still don't have a total count, try to fetch it
                const currentTotal = apiTotalCount || totalStoredImages;
                if (currentTotal === 0 && !append) {
                    try {
                        const hashes = await getCollectionHashes(accountNo);
                        if (hashes && Array.isArray(hashes)) {
                            setApiTotalCount(hashes.length);
                            setTotalStoredImages(hashes.length);
                        }
                    } catch (error) {
                        console.error("Error fetching total count:", error);
                    }
                }
            } else {
                // Fallback to empty array if no response
                console.error('[Collection Refresh Images] No response from API for collection images');
                
                // setLoadingImages(false);
                // setImageList([]);
                // setTotalImages(0);
                // setFilteredTotalCount(0);
            }
            if (!append) {
                setPageLoading(false);
            }
            setLoadingMore(false);
        } catch (error) {
            console.error("Error fetching collection from API:", error);
            if (!append) {
                setImageList([]);
                setTotalImages(0);
                setFilteredTotalCount(0);
                setPageLoading(false);
            }
            setLoadingMore(false);
            setHasMore(false);
            setLoadingImages(false);
        }
        // if (accountContext.loggedIn && accountContext.accountDetails?.accountNo) {
        // } else {
        //     // Not logged in, show empty collection
        //     console.log(`TELL ME WHY`);
            
        //     setImageList([]);
        //     setTotalImages(0);
        //     setFilteredTotalCount(0);
        // }
    }

    useEffect(() => {
        db.settings.where("key").equals("enable_animations").first(setting => {
            setAnimations(setting?.value ?? false)
        })
        setTotalImages(collectionContext.totalImages);
    }, [collectionContext.totalImages])

    /**
     * Clears the generated media collection (images, videos, audio)
     * @returns {Promise<void>}
     */
        const doClearGenerated = async () => {
            Swal.fire({
                color: "#190A4E",
                title: "Clear all saved media files (images, videos, audio)? This cannot be undone.",
                icon: "info",
                confirmButtonText: 'Confirm',
                confirmButtonColor: "#FFB02E",
                showDenyButton: true,
                denyButtonText: "Cancel",
                denyButtonColor: "#190A4E",
            }).then((result) => {
                if (result.isConfirmed) {
                    // Note: This would need a separate API endpoint to clear all images server-side
                    // For now, we'll just refresh the view
                    setImageList([]);
                    setTotalImages(0);
                    setApiTotalCount(0);
                    setTotalStoredImages(0);
                    setFilteredTotalCount(0);
                    getTotalStoredImages();
                }
            });
        }

    // Handle initial load on component mount (including page refresh)
    useEffect(() => {
        // if (accountContext.loggedIn) {
        //     setLoadingImages(true);
        // }
        // if (sessionStorage.getItem('initialCollectionLoad') || sessionStorage.getItem('initialCollectionLoad') === "true") {
        //     setLoadingImages(true);
        // }
        (async()=>{
            await getTotalStoredImages().then(async() => {
                sessionStorage.setItem('finishedCollectionLoad', 'true');
                console.log("Finished loading images from the server MC");
                // collectionContextDispatch({type: 'resetLoading'});
            })

        })()
        // if (collectionContext.finishedLoading || sessionStorage.getItem('finishedCollectionLoad') == "true") {
        // }
    }, [collectionContext.finishedLoading, accountContext.loggedIn, accountContext.accountDetails?.accountNo]);

    //useEffect(() => {
    //    refreshImages();
    //}, [page, showAll, category, sortMethod]);

    useEffect(() => {
        // Reset infinite scroll when changing filters or toggling showAll
        setInfiniteScrollPage(1);
        setHasMore(true);
        refreshImages()
    }, [page, showAll, category, sortMethod]);

    // Separate effect for infinite scroll page changes
    useEffect(() => {
        if (infiniteScrollPage > 1 && showAll) {
            refreshImages(true);
        }
    }, [infiniteScrollPage]);

    const fetchTags = async () => {
        const tempSet = new Set();
        
        // Extract tags from the current imageList which comes from API
        imageList.forEach(record => {
            if (record.tag) {
              record.tag.split(",").forEach(tag => {
                if (tag) {
                  tempSet.add(tag.trim());
                }
              });
            } else {
              setEmpty(true);
            }
        });
          
        let uniqueArray = [];
        let lowercasedArray = [];
        
        Array.from(tempSet).forEach(value => {
            if (!lowercasedArray.includes(value.toLowerCase())) {
                uniqueArray.push(value);
                lowercasedArray.push(value.toLowerCase());
            }
        });

        lowercasedArray = null;
        
        setCategories(uniqueArray.sort((a, b) => a.localeCompare(b)));
        collectionContextDispatch({type: 'collectionTagging', tags: uniqueArray});
            
        
        // db.collectedImages.toArray((images) => {
        //     const tempSet = new Set();
        //     images.forEach(image => {
        //       if (image.tag) {
        //         image.tag.split(",").forEach(tag => {
        //           if (tag) {
        //             tempSet.add(tag.trim());
        //           }
        //         });
        //       } else {
        //         setEmpty(true);
        //       }
        //     });
          
        //     let uniqueArray = [];
        //     let lowercasedArray = [];

        //     Array.from(tempSet).forEach(value => {
        //         if (!lowercasedArray.includes(value.toLowerCase())) {
        //             uniqueArray.push(value);
        //             lowercasedArray.push(value.toLowerCase());
        //         }
        //     });

        //     lowercasedArray = null;
            
        //     setCategories(uniqueArray.sort((a, b) => a.localeCompare(b)));
        //     collectionContextDispatch({type: 'collectionTagging', tags: uniqueArray});
        //   });
    }

    useEffect(() =>{
        fetchTags();
    },[imageList])

    useEffect(() => {
        if (inputImage !== null) {
            collectionContextDispatch({type: 'collectionImage', payload: inputImage})
            navigate("/");
        }     
    }, [inputImage])

    const getNumColumns = () => {
        switch(columnClass) {
            case "cols-1":
                return 1;
            case "cols-2":
                return 2;
            case "cols-3":
                return 3;
            default:
            case "cols-4":
                return 4;
            case "cols-5":
                return 5;
            case "cols-6":
                return 6;
        }
    }

    const saveCollection = async () => {
        // With API, saving is handled server-side
        // Just refresh the current view
        getTotalStoredImages();
        setSaveImages(false);
    }

    const setGeneratePrompt = (prompt) => {
        console.log(prompt);
        let generate;
        try {
            generate = JSON.parse(localStorage.getItem("Generate"))
        } catch (error) {
            console.error("Error fetching the generate value in Collection: ", error)
        }
        // let generate = JSON.parse(localStorage.getItem('Generate'));
        try {
            localStorage.setItem('Generate', JSON.stringify({
                prompt: prompt,
                enhancePrompt: generate.enhancePrompt,
                seed: generate.seed,
                negativeKeywords: generate.negativeKeywords,
                steps: generate.steps,
                guidance: generate.guidance,
                imageCount: generate.imageCount,
                imageWidth: generate.imageWidth,
                imageHeight: generate.imageHeight,
                selectedModel: generate.selectedModel,
                selectedScheduler: generate.selectedScheduler,
                tile: generate.tile,
                shuffle: generate.shuffle,
                upscale: generate.upscale,
            }));
        } catch (error) {
            console.error("Error saving to Generate value in Collection: ", error)
        }
        setTimeout(() => {
            navigate("/");
        }, 250);
    }

    const handleEscapeKey = (e) => {
        if (e.key === "Escape") {
            setZoomedImage(null)
        }
    }

    useEffect(() => {
        if (zoomedImage) {
            window.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('keydown', handleEscapeKey);
        };
    }, [zoomedImage]);

    const getMediaTypeFromExtension = (filename) => {      
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
        const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'];
      
        if (imageExtensions.includes(filename)) return 'image';
        if (videoExtensions.includes(filename)) return 'video';
        if (audioExtensions.includes(filename)) return 'audio';
      
        return 'unknown';
      }
      
      function MediaCard({ image, ...rest }) {
        const [kind, setKind] = useState(null); // "Image" | "Video" | "Audio" | "application/..." | "Invalid Input"
      
        useEffect(() => {
          let cancel = false;
          (async () => {
            try {
              const result = await fetchMediaType(image.image);
              if (!cancel) setKind(result);
            } catch (e) {
              if (!cancel) setKind("Invalid Media");
            }
          })();
          return () => { cancel = true; };
        }, [image]);
      
        if (!kind) return <Placeholder Count={1} />;
      
        if (kind.startsWith("Video")) {
          return <Video {...image} {...rest} />;
        }
        if (kind.startsWith("Audio")) {
          return <Audio {...image} name={image.prompt || "Audio File"} {...rest} />;
        }
        return <CollectionImage {...image} {...rest} />;
      }  

    return (
        <div className={"px-4 pb-4 relative"}>
            <h1 className={"text-lg md:text-xl py-2 font-bold flex flex-col sm:flex-row items-start sm:items-center gap-2"}>
                <div className="flex items-center w-full sm:w-auto">
                    <span>My Collection</span>
                    <hr className={"hidden sm:block flex-grow border border-white/40 bg-white/40 ml-2"} />
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <select className={"rounded-lg text-dark-blue border-0 p-2 text-sm w-32 sm:w-36"} value={sortMethod} onChange={(e) => setSortMethod(e.target.value)}>
                        <option value="Newest" key={"sort_1"}>From Newest</option>
                        <option value="Oldest" key={"sort_2"}>From Oldest</option>
                        <option value={"Non-Tagged-Asc"} key={"sort_3"}>Non-Tagged - New</option>
                        <option value={"Non-Tagged-Desc"} key={"sort_4"}>Non-Tagged - Old</option>
                        <option value={"SH-Images-Asc"} key={"sort_5"}>SH - New</option>
                        <option value={"SH-Images-Desc"} key={"sort_6"}>SH - Old</option>
                    </select>
                    <select className={"rounded-lg text-dark-blue border-0 p-2 text-sm w-24 sm:w-28"} value={category} onChange={(e) => setCategory(e.target.value)}>
                        <option value={"All"} key={"category_1"}>All</option>
                        <option value={"Public"} key={"category_2"}>Public</option>
                        <option value={"PublicTags"} key={"category_3"}>Pub-Tag</option>
                        <option value={"PublicNoTags"} key={"category_4"}>Pub-None</option>
                        <option value={"Private"} key={"category_5"}>Private</option>
                        <option value={"PrivateTags"} key={"category_6"}>Prv-Tag</option>
                        <option value={"PrivateNoTags"} key={"category_7"}>Prv-None</option>
                        {categories.map((category, i) => <option value={`${category}`} key={`category ${i+7}`}>{category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()}</option>)}
                    </select>
                    <button className={"py-2 px-3 md:px-4 border rounded bg-red-400 transition text-sm"} onClick={doClearGenerated}>
                        <span className="hidden sm:inline">Delete All</span>
                        <span className="sm:hidden">Delete</span>
                    </button>
                </div>
                <div className={"flex flex-row justify-between sm:justify-end items-center w-full sm:w-auto p-2 md:p-4"}>
                    {!showAll ?
                        <>
                            {page > 0 ?
                                <button className={"text-4xl p-2"} onClick={() => setPage(page - 1)}>
                                    <img src={DoubleLeftIcon} alt="Previous Page" className='h-10'/>
                                </button> 
                                : 
                                <button className={"text-4xl p-2 text-gray-400"} >
                                    <img src={GreyDoubleLeftIcon} alt="Previous Page" className='h-10'/>
                                </button>}
                            <div className={"text-xl p-2"} >{page + 1}</div>
                            {(() => {
                                const isFiltering = category !== "All" || sortMethod.includes("Non-Tagged") || sortMethod.includes("SH-Images");
                                const totalToUse = isFiltering ? filteredTotalCount : (apiTotalCount || totalStoredImages);
                                return (page * 20 + 20) < totalToUse ? (
                                    <button className={"text-4xl p-2"} onClick={() => setPage(page + 1)}>
                                        <img src={DoubleRightIcon} alt="Next Page" className='h-10'/>
                                    </button>
                                ) : (
                                    <button className={"text-4xl p-2 text-gray-400"} >
                                        <img src={GreyDoubleRightIcon} alt="Next Page" className='h-10'/>
                                    </button>
                                );
                            })()}
                        </>
                            : 
                        null}
                    <button className={"py-2 px-4 mx-1 border rounded hover:bg-dark-blue transition"} onClick={() => setShowAll(!showAll)}>
                        {showAll ? "Show Pages" : "Show All (Infinite Scroll)"}
                    </button>
                    <span className={"font-light mr-5 text-sm"}>
                        {(category !== "All" || sortMethod.includes("Non-Tagged") || sortMethod.includes("SH-Images")) ? 
                            <>Filtered: <span className={"font-bold"}>{showAll ? `${imageList.length}/${filteredTotalCount}` : `${filteredTotalCount}/${apiTotalCount || totalStoredImages}`}</span></> :
                            <>Total Media: <span className={"font-bold"}>{showAll ? `${imageList.length}/${apiTotalCount || totalStoredImages}` : (apiTotalCount || totalStoredImages)}</span></>
                        }
                    </span>
                    <select className={"rounded-lg text-dark-blue border-0 p-2"}
                            value={columnClass} onChange={(e) => setColumnClass(e.target.value)}>
                        <option value={"cols-2"}>2 Columns</option>
                        <option value={"cols-3"}>3 Columns</option>
                        <option value={"cols-4"}>4 Columns</option>
                        <option value={"cols-5"}>5 Columns</option>
                        <option value={"cols-6"}>6 Columns</option>
                    </select>
                </div>
            </h1>
                {loading ? 
                    <>
                        {animations ?
                            <div className="w-2/3 mx-auto">
                                <lottie-player
                                autoplay
                                loop
                                mode="normal"
                                src={PromptSpin}
                                ></lottie-player>
                            </div>
                        :
                            <img src={Logo} alt={"Promptus Logo"} className="w-2/3 mx-auto animate-pulse" />  
                        }
                    </>             
                :    
                <>
                    {accountContext.loggedIn ? 
                        <div className="relative">
                            {pageLoading && !loading && (
                                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                            <span className="text-white text-sm">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <Masonry
                                breakpointCols={{
                                    default: getNumColumns(),
                                    1920: Math.min(getNumColumns(), 6),
                                    1536: Math.min(getNumColumns(), 5),
                                    1280: Math.min(getNumColumns(), 4),
                                    1024: Math.min(getNumColumns(), 4),
                                    768: Math.min(getNumColumns(), 3),
                                    640: Math.min(getNumColumns(), 2),
                                    320: 1
                                }}
                                className="masonry-grid p-4"
                                columnClassName="masonry-grid_column"
                            >
                        {imageList.map((image, i) => {
                            return (
                            <InView key={`media_${i}_${image.id}`} triggerOnce threshold={0.1}>
                                {({ inView, ref, entry }) => (
                                    <div ref={ref}>
                                        {inView ? (
                                            <MediaCard
                                                image={image}                    // <-- use image, not props.image
                                                isMyCollection
                                                inCollection
                                                dbTable={db.collectedImages}
                                                tagArray={collectionContext.collectionTags}
                                                refreshTags={fetchTags}
                                                refreshImages={getTotalStoredImages}
                                                setSaveImages={setSaveImages}
                                                setZoomedImage={setZoomedImage}
                                                setUid={setGeneratePrompt}
                                                setImage={(img) => setInputImage({ image: img })}
                                            />
                                        ) : (
                                            <Placeholder Count={1} />
                                        )}
                                    </div>
                                )}
                            </InView>    
                            )
                        } 
                        )}
                        </Masonry>
                        
                        {/* Infinite scroll sentinel */}
                        {showAll && hasMore && (
                            <InView
                                onChange={(inView) => {
                                    if (inView && !loadingMore && hasMore) {
                                        loadMoreImages();
                                    }
                                }}
                                threshold={0}
                            >
                                <div className="flex justify-center py-8">
                                    <div className="flex items-center space-x-3">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                        <span className="text-white">Loading more...</span>
                                    </div>
                                </div>
                            </InView>
                        )}
                        
                        {/* End of list message */}
                        {showAll && !hasMore && imageList.length > 0 && (
                            <div className="flex justify-center py-8">
                                <span className="text-white/60">No more items to load</span>
                            </div>
                        )}
                        </div>
                    :    
                        <h1>Please login before viewing collection</h1>
                    }
                </>
            }
            
            {zoomedImage ?
                <div className={"fixed inset-0 bg-dark-blue/30 backdrop-blur-sm z-50 flex flex-col place-items-center justify-center"} onClick={() => setZoomedImage(null)} onKeyUp={(e) => handleEscapeKey(e)}>
                    <div className={"relative"}>
                        <button className={"absolute top-0 right-0 px-5 py-2 bg-dark-blue/40 rounded-lg hover:bg-dark-blue transition"} type={"button"}>Close</button>
                        <img className={"w-auto max-w-screen-md rounded-lg"} src={zoomedImage.image && zoomedImage.image.includes(":\\") ? toFileUrl(zoomedImage.image) : `data:image/png;base64,${zoomedImage.image}`} alt={`Generated image: ${ zoomedImage.seed }`} />
                    </div>
                </div>
                : null}
        </div>
    );
}
